/**
 * Signature Series 3D Configurator
 * Panel-based 3D viewer for modular building products
 * Loads individual panel models and assembles them in the scene
 */
import * as THREE from 'three';
import { SceneManager } from './SceneManager.js';
import { ModelLoader } from './ModelLoader.js';
import { MaterialManager } from './MaterialManager.js';
import { CameraControls } from './CameraControls.js';

const DEBUG = false;
const log = (...args) => DEBUG && console.log('[Signature3D]', ...args);

class SignatureConfigurator3D {
  constructor(container, configData) {
    if (!container) {
      throw new Error('SignatureConfigurator3D: Container element is required');
    }
    
    this.container = container;
    this.configData = configData || {};
    this.sceneManager = null;
    this.modelLoader = null;
    this.materialManager = null;
    this.cameraControls = null;
    this.currentStep = 1;
    this.isInitialized = false;
    this.isLoading = false;
    
    // Panel tracking
    this.loadedPanels = new Map(); // panelId -> THREE.Object3D
    this.currentConfig = {};
    
    // Extract settings from config
    this.loadingSequence = configData?.loadingSequence || [];
    this.materialGroups = configData?.materialGroups || {};
    this.sceneSettings = configData?.sceneSettings || {};
  }

  init() {
    if (this.isInitialized) {
      log('SignatureConfigurator3D already initialized');
      return this;
    }
    
    if (!this.container) {
      console.error('SignatureConfigurator3D: Cannot initialize without container');
      return this;
    }

    try {
      // Initialize SceneManager (does not accept options)
      this.sceneManager = new SceneManager(this.container);
      
      // Apply custom scene settings AFTER initialization
      this.applySceneSettings();
      
      this.modelLoader = new ModelLoader(this.sceneManager, this.configData);
      this.materialManager = new MaterialManager(this.modelLoader, this.configData, this.sceneManager);
      this.cameraControls = new CameraControls(this.sceneManager);
      
      this.isInitialized = true;
      log('SignatureConfigurator3D initialized');
    } catch (error) {
      console.error('Failed to initialize 3D viewer:', error);
      this.isInitialized = false;
      this.dispatchError('initialization', error);
    }
    
    return this;
  }

  /**
   * Apply custom scene settings from config (camera, ground, etc.)
   * Mobile-responsive: increases camera distance on small screens for better full-model view
   */
  applySceneSettings() {
    if (!this.sceneManager) return;

    const settings = this.sceneSettings;
    
    // Mobile detection for responsive camera distance
    const isMobile = (window.innerWidth || document.documentElement.clientWidth) <= 768;
    
    // Apply camera distance and target
    // Mobile needs MORE distance (more zoomed out) to see the full model
    const baseCameraDistance = settings.cameraDistance || 250;
    const cameraDistance = isMobile ? baseCameraDistance * 1.6 : baseCameraDistance;
    const cameraTarget = settings.cameraTarget || { x: 0, y: 40, z: 0 };
    
    // Set controls target
    if (this.sceneManager.controls) {
      this.sceneManager.controls.target.set(
        cameraTarget.x || 0,
        cameraTarget.y || 40,
        cameraTarget.z || 0
      );
    }
    
    // Position camera at 45 degree angle
    const angle = Math.PI / 4;
    this.sceneManager.camera.position.set(
      cameraDistance * Math.cos(angle),
      cameraDistance * 0.6,
      cameraDistance * Math.sin(angle)
    );
    
    if (this.sceneManager.controls) {
      this.sceneManager.controls.update();
    }
    
    // Ground plane sizing is now handled by fitFloorToModel() after panels load
    // This ensures the floor matches the actual model bounds, not just config dimensions
    
    log('Applied scene settings:', settings, 'isMobile:', isMobile, 'cameraDistance:', cameraDistance);
  }

  dispatchError(type, error) {
    this.container?.dispatchEvent(new CustomEvent('configurator:error', {
      detail: { type, error: error.message || error, timestamp: Date.now() }
    }));
  }

  /**
   * Load all panels based on current configuration
   * @param {Object} config - Current configuration object
   */
  async loadPanels(config = {}) {
    if (!this.isInitialized || this.isLoading) return;

    this.isLoading = true;
    
    // Merge with default configuration to ensure we have all values
    const defaultConfig = this.configData?.defaultConfiguration || {};
    this.currentConfig = { ...defaultConfig, ...config };
    
    log('Loading panels with config:', this.currentConfig);
    log('Loading sequence:', this.loadingSequence);
    
    this.container.dispatchEvent(new CustomEvent('configurator:modelLoading', {
      detail: { config: this.currentConfig }
    }));

    try {
      // Clear existing panels
      this.clearPanels();
      
      // Sort loading sequence by order
      const sortedSequence = [...this.loadingSequence].sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Load panels in sequence
      for (const item of sortedSequence) {
        await this.loadSequenceItem(item, this.currentConfig);
      }

      log('Loaded panels:', Array.from(this.loadedPanels.keys()));

      // Fit camera and floor FIRST (before materials, so floor sizing is correct)
      this.fitCameraToModel();

      // Apply initial materials AFTER floor is sized
      try {
        await this.applyMaterials(this.currentConfig);
      } catch (materialError) {
        console.warn('Error applying materials (non-blocking):', materialError);
      }

      this.setViewForStep(this.currentStep);
      
      // FINAL floor visibility check - ensure Shell Only has no floor
      const interiorPackage = this.currentConfig?.interiorPackage || 
                             this.configData?.defaultConfiguration?.interiorPackage || 
                             'shell-only';
      log('Final floor check - interiorPackage:', interiorPackage);
      if (interiorPackage === 'shell-only') {
        this.hideFloor();
      }
      
      // Always call onModelLoaded to hide loading indicator
      this.onModelLoaded();
    } catch (error) {
      console.error('Error loading panels:', error);
      this.dispatchError('loading', error);
      // Still try to hide loading indicator even on error
      this.onModelLoaded();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Fit camera to show all loaded panels
   * Mobile-responsive: increases camera distance on small screens for better full-model view
   */
  fitCameraToModel() {
    if (this.loadedPanels.size === 0) return;

    const box = new THREE.Box3();
    
    this.loadedPanels.forEach(({ model }) => {
      box.expandByObject(model);
    });

    if (box.isEmpty()) return;

    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    
    // Mobile detection for responsive camera distance
    const isMobile = (window.innerWidth || document.documentElement.clientWidth) <= 768;
    
    // Use scene settings for camera if available
    // Mobile needs MORE distance (more zoomed out) to see the full model
    const baseCameraDistance = this.sceneSettings.cameraDistance || 250;
    const cameraDistance = isMobile ? baseCameraDistance * 1.6 : baseCameraDistance;
    const cameraTarget = this.sceneSettings.cameraTarget || { x: 0, y: 40, z: 0 };
    
    // Set controls target
    if (this.sceneManager.controls) {
      this.sceneManager.controls.target.set(
        cameraTarget.x || center.x,
        cameraTarget.y || size.y / 3,
        cameraTarget.z || center.z
      );
    }
    
    // Position camera at 45 degree angle
    const angle = Math.PI / 4;
    this.sceneManager.camera.position.set(
      cameraDistance * Math.cos(angle),
      cameraDistance * 0.6,
      cameraDistance * Math.sin(angle)
    );
    
    if (this.sceneManager.controls) {
      this.sceneManager.controls.update();
    }

    log('Camera fitted to model, center:', center, 'size:', size, 'isMobile:', isMobile, 'cameraDistance:', cameraDistance);
    
    // Fit the floor to the wall panels (excluding roof for accurate sizing)
    this.fitFloorToModel();
  }

  /**
   * Fit the floor/ground plane to match the model's interior dimensions
   * 
   * Floor System (Reference Specification):
   * - Floor size = length × 12 by depth × 12 (from model dimensions in feet)
   * - Floor thickness = 1 inch
   * - Floor position Y = -9.75
   * - Centered at (0, 0, 0) for X and Z
   * - Floor must stay completely inside the shed footprint
   * 
   * CRITICAL: Uses exact model dimensions (interior), no insets
   */
  fitFloorToModel() {
    if (!this.sceneManager.ground) return;
    
    // Get model dimensions from config (in feet)
    const modelDimensions = this.configData?.model?.dimensions || {};
    
    // Dimension mapping: "8x10" = depth 8 (Z-axis), length 10 (X-axis)
    let depth, length;
    if (modelDimensions.length !== undefined && modelDimensions.depth !== undefined) {
      length = modelDimensions.length;
      depth = modelDimensions.depth;
    } else {
      // Fallback to width/depth naming convention
      depth = modelDimensions.width || 8;
      length = modelDimensions.depth || 10;
    }
    
    // Floor size = length × 12 by depth × 12 (EXACT dimensions from config)
    const floorWidth = length * 12;   // X-axis (length in inches)
    const floorDepth = depth * 12;    // Z-axis (depth in inches)
    
    // Floor position: centered at (0, 0, 0) with Y = -9.75
    const floorX = 0;
    const floorY = -9.75;  // Standard floor height (foundation top level)
    const floorZ = 0;
    
    // Save the current material before resizing geometry
    const currentMaterial = this.sceneManager.ground.material;
    
    // Resize the ground geometry (PlaneGeometry for textured floor)
    this.sceneManager.ground.geometry.dispose();
    this.sceneManager.ground.geometry = new THREE.PlaneGeometry(floorWidth, floorDepth);
    
    // Ensure material is preserved
    if (currentMaterial) {
      this.sceneManager.ground.material = currentMaterial;
    }
    
    // Position the floor centered at origin with correct Y height
    this.sceneManager.ground.position.set(floorX, floorY, floorZ);
    
    // Ensure floor rotation is flat (horizontal plane facing up)
    // Rotation: -90° on X-axis to make plane horizontal
    this.sceneManager.ground.rotation.set(-Math.PI / 2, 0, 0);
    
    // Only make floor visible if NOT shell-only
    const interiorPackage = this.currentConfig?.interiorPackage || 
                           this.configData?.defaultConfiguration?.interiorPackage || 
                           'shell-only';
    this.sceneManager.ground.visible = (interiorPackage !== 'shell-only');
    
    log(`Interior Floor: ${floorWidth}" x ${floorDepth}" (${length}' x ${depth}') at (${floorX}, ${floorY}, ${floorZ}), visible: ${this.sceneManager.ground.visible}`);
  }

  /**
   * Calculate dynamic panel positions based on model dimensions
   * 
   * Signature Model Positioning System:
   * - Coordinate System: X (left-right), Y (height), Z (front-back)
   * - Units: Inches (feet × 12)
   * - For "8x10": depth=8 (Z-axis), length=10 (X-axis)
   * 
   * Panel Rotations: Front/Back=0°, Left=270°, Right=90°, Roof=90°
   */
  calculateDynamicPositions() {
    const modelDimensions = this.configData?.model?.dimensions || {};
    
    // Dimension mapping: "8x10" = depth 8 (Z-axis), length 10 (X-axis)
    let depth, length;
    if (modelDimensions.length !== undefined && modelDimensions.depth !== undefined) {
      length = modelDimensions.length;
      depth = modelDimensions.depth;
    } else {
      depth = modelDimensions.width || 8;
      length = modelDimensions.depth || 10;
    }
    
    const lengthInches = length * 12;
    const depthInches = depth * 12;
    const panelY = -9;
    
    // Signature-specific alignment offsets
    const backXOffset = -lengthInches + 4;
    
    log(`Signature positions: ${depth}x${length} (${depthInches}" x ${lengthInches}")`);
    
    return {
      frontPanel: {
        x: -(lengthInches / 2) + 4,
        y: panelY,
        z: depthInches / 2,
        rotationY: 0
      },
      backPanel: {
        x: (lengthInches / 2) + backXOffset,
        y: panelY,
        z: -(depthInches / 2) + 0.5,
        rotationY: 0
      },
      leftPanel: {
        x: -(lengthInches / 2) + 0.5,
        y: panelY,
        z: -(depthInches / 2),
        rotationY: 270
      },
      rightPanel: {
        x: (lengthInches / 2) - 0.5,
        y: panelY,
        z: depthInches / 2,
        rotationY: 90
      },
      roof: {
        x: 0,
        y: -9,
        z: 6.5,
        rotationY: 90
      }
    };
  }

  /**
   * Load a single item from the loading sequence
   */
  async loadSequenceItem(item, config) {
    const { type, id, rotation } = item;
    
    // Get selected value or fall back to default
    let selectedValue = config[id];
    if (!selectedValue) {
      const section = this.findSectionData(id);
      selectedValue = section?.defaultValue || this.configData?.defaultConfiguration?.[id];
    }

    const optionData = this.findOptionData(id, selectedValue);
    if (!optionData?.modelFile) {
      if (type !== 'roof' && id !== 'roofAddon') {
        console.warn(`No model file for ${id}:${selectedValue}`);
      }
      return;
    }

    log(`Loading ${id}: ${selectedValue}`);

    try {
      const objUrl = optionData.modelFile;
      const mtlUrl = objUrl.replace('.obj', '.mtl');
      const model = await this.modelLoader.loadObjWithMtl(objUrl, mtlUrl);
      
      if (model) {
        const useNativePositions = this.sceneSettings?.useNativePositions === true;
        
        if (!useNativePositions) {
          // Calculate and apply dynamic positioning for Signature model
          const dynamicPositions = this.calculateDynamicPositions();
          const panelPosition = dynamicPositions[id] || { x: 0, y: 0, z: 0, rotationY: 0 };
          
          // Apply rotation first (critical for panel alignment)
          if (panelPosition.rotationY !== undefined) {
            model.rotation.y = panelPosition.rotationY * Math.PI / 180;
          } else if (rotation) {
            model.rotation.set(
              (rotation.x || 0) * Math.PI / 180,
              (rotation.y || 0) * Math.PI / 180,
              (rotation.z || 0) * Math.PI / 180
            );
          }
          
          // Then apply position
          model.position.set(panelPosition.x || 0, panelPosition.y || 0, panelPosition.z || 0);
        }
        
        // Apply global scale if specified
        if (this.sceneSettings.scale && this.sceneSettings.scale !== 1) {
          model.scale.setScalar(this.sceneSettings.scale);
        }

        // Enable shadows
        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });

        // Add to scene and track
        this.sceneManager.scene.add(model);
        this.loadedPanels.set(id, { model, optionData, item, value: selectedValue });
        log(`✓ Loaded ${id}`);
      }
    } catch (error) {
      console.error(`Failed to load panel ${id}:`, error);
    }
  }

  /**
   * Find option data for a given section and value
   */
  findOptionData(sectionId, value) {
    if (!this.configData?.steps) return null;

    for (const step of this.configData.steps) {
      const section = step.sections?.find(s => s.id === sectionId);
      if (section) {
        // If value provided, find matching option
        if (value) {
          return section.options?.find(o => o.value === value);
        }
        // Otherwise return default or first option
        const defaultValue = this.configData.defaultConfiguration?.[sectionId];
        if (defaultValue) {
          return section.options?.find(o => o.value === defaultValue);
        }
        return section.options?.[0];
      }
    }
    return null;
  }

  /**
   * Clear all loaded panels
   */
  clearPanels() {
    this.loadedPanels.forEach(({ model }) => {
      // Use scene.remove directly (SceneManager doesn't have removeFromScene method)
      this.sceneManager.scene.remove(model);
      // Inline disposal to avoid dependency on ModelLoader.disposeModel
      this.disposeModelInline(model);
    });
    this.loadedPanels.clear();
  }

  /**
   * Dispose of a model and its resources (inline to avoid modifying shared ModelLoader)
   */
  disposeModelInline(model) {
    if (!model) return;
    
    model.traverse((child) => {
      if (child.isMesh) {
        if (child.geometry) {
          child.geometry.dispose();
        }
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => {
              if (m.map) m.map.dispose();
              if (m.dispose) m.dispose();
            });
          } else {
            if (child.material.map) child.material.map.dispose();
            if (child.material.dispose) child.material.dispose();
          }
        }
      }
    });
  }

  /**
   * Apply materials based on configuration
   */
  async applyMaterials(config) {
    log('Applying materials with config:', config);

    // Use defaults from config data if not in current config
    const defaults = this.configData?.defaultConfiguration || {};
    const sidingType = config.siding || defaults.siding || 'lap';
    const sidingColor = config.sidingColor || defaults.sidingColor || 'iron-gray';
    const trimColor = config.trimColor || defaults.trimColor || 'aluminum';
    const doorColor = config.doorColor || defaults.doorColor || 'factory-primed-white';
    const fasciaColor = config.fasciaColor || defaults.fasciaColor || 'iron-gray';
    const soffitColor = config.soffitColor || defaults.soffitColor || 'iron-gray';
    const eavesColor = config.eavesColor || defaults.eavesColor || 'unfinished-eaves';
    const mainFloor = config.mainFloorFinish || defaults.mainFloorFinish || 'ashlar-oak';
    const exteriorAddon = config.exteriorAddon || defaults.exteriorAddon || 'none';

    // Store exteriorAddon in currentConfig (needed by applySidingMaterial)
    this.currentConfig.exteriorAddon = exteriorAddon;
    log(`Exterior addon state: ${exteriorAddon}`);

    // IMPORTANT: Hide metal wainscot meshes initially (unless addon is selected)
    this.hideMetalWainscotMeshes(exteriorAddon !== 'metal-wainscot');

    // Apply siding texture and color
    await this.applySidingMaterial(sidingType, sidingColor);

    // Apply trim color (comprehensive - affects all trim elements, metal wainscot, roof metal)
    this.applyTrimColor(trimColor);

    // Apply door color
    this.applyColorToGroup('door_color', doorColor);

    // Apply fascia color
    this.applyColorToGroup('fascia', fasciaColor);

    // Apply soffit color
    this.applyColorToGroup('soffit', soffitColor);

    // Apply eaves color (combines fascia and soffit elements)
    log('Applying eaves color:', eavesColor);
    this.applyColorToGroup('eaves', eavesColor);

    // Check interior package - only show floor for non-shell packages
    const interiorPackage = config.interiorPackage || defaults.interiorPackage || 'shell-only';
    if (interiorPackage === 'shell-only') {
      // Shell Only: hide floor (unfinished interior)
      this.hideFloor();
      log('Floor hidden for Shell Only package');
    } else {
      // Lifestyle packages: show floor with texture
      await this.applyFloorFinish(mainFloor);
      log(`Floor shown for ${interiorPackage} package`);
    }

    // Apply glass material to windows
    this.applyGlassMaterial();

    // Apply roof material
    this.applyRoofMaterial();

    log('Materials applied successfully');
  }

  /**
   * Apply roof material
   */
  applyRoofMaterial() {
    const groupConfig = this.materialGroups.roof_material;
    if (!groupConfig) return;

    const meshes = this.findMeshesByNames(groupConfig.meshNames);
    if (!meshes.length) {
      // Try to find roof meshes by panel name
      const roofPanel = this.loadedPanels.get('roof');
      if (roofPanel?.model) {
        const roofMeshes = [];
        roofPanel.model.traverse(child => {
          if (child.isMesh) roofMeshes.push(child);
        });
        if (roofMeshes.length) {
          const color = new THREE.Color(groupConfig.defaultColor || '#1A1A1A');
          const material = new THREE.MeshStandardMaterial({
            color,
            roughness: 0.8,
            metalness: 0.1
          });
          roofMeshes.forEach(mesh => {
            mesh.material = material.clone();
          });
        }
      }
      return;
    }

    const color = new THREE.Color(groupConfig.defaultColor || '#1A1A1A');
    meshes.forEach(mesh => {
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(m => { if (m.color) m.color = color; });
        } else if (mesh.material.color) {
          mesh.material.color = color;
        }
      }
    });
  }

  /**
   * Apply siding material with texture and color
   * Handles Block, Plank, Lap, Cedar Plank, and Cedar Shake siding
   * Supports Metal Wainscot interaction (standard vs metal mesh sets)
   */
  async applySidingMaterial(sidingType, colorValue, textureUrlOverride = null, hexColorOverride = null) {
    log('===== applySidingMaterial =====');
    log('sidingType:', sidingType);
    log('colorValue:', colorValue);
    log('textureUrlOverride:', textureUrlOverride);
    log('hexColorOverride:', hexColorOverride);
    
    const defaults = this.configData?.defaultConfiguration || {};
    const actualSidingType = sidingType || this.currentConfig.siding || defaults.siding || 'lap';
    const actualColorValue = colorValue || this.currentConfig.sidingColor || defaults.sidingColor || 'iron-gray';
    const hasMetalWainscot = this.currentConfig.exteriorAddon === 'metal-wainscot';
    
    log('actualSidingType:', actualSidingType);
    log('actualColorValue:', actualColorValue);
    log('hasMetalWainscot:', hasMetalWainscot);
    
    log(`Applying siding: ${actualSidingType}, color: ${actualColorValue}, wainscot: ${hasMetalWainscot}`);
    
    // Siding mesh configurations - each type has standard and metal (wainscot) versions
    const sidingMeshConfig = {
      'block': {
        standard: ['block_siding', 'block_siding_color', 'block_siding_fastener', 'block_siding_trims'],
        metal: ['block_siding_metal', 'block_siding_metal_color', 'block_siding_metal_fastener', 'block_siding_metal_trims'],
        colorMeshes: ['block_siding_color', 'block_siding_metal_color'],
        fastenerMeshes: ['block_siding_fastener', 'block_siding_metal_fastener'],
        trimMeshes: ['block_siding_trims', 'block_siding_metal_trims'],
        hasTexture: false,
        usesSidingColor: true
      },
      'plank': {
        standard: ['plank_siding', 'plank_siding_color', 'plank_trims', 'plank_trim', 'plank_siding_trims'],
        metal: ['plank_siding_metal', 'plank_siding_metal_color', 'plank_siding_metal_trims'],
        colorMeshes: ['plank_siding_color', 'plank_siding', 'plank_siding_metal', 'plank_siding_metal_color'],
        trimMeshes: ['plank_siding_trims', 'plank_siding_metal_trims', 'plank_trims', 'plank_trim'],
        hasTexture: false,
        usesSidingColor: true
      },
      'lap': {
        standard: ['wood_texture', 'wood_texture_trims'],
        metal: ['wood_texture_metal', 'wood_texture_metal_trims'],
        colorMeshes: ['wood_texture', 'wood_texture_metal'],
        trimMeshes: ['wood_texture_trims', 'wood_texture_metal_trims'],
        hasTexture: true,
        usesSidingColor: true,
        textureScale: { u: 1, v: 0.142 }, // Horizontal boards (one stretch horizontal, repeated vertical)
        textureUrl: 'https://cdn.shopify.com/s/files/1/0969/5304/1190/files/base_lap.png' // Override JSON texture with base-lap
      },
      'horiz_plank': {
        standard: ['wood_texture', 'wood_texture_trims'],
        metal: ['wood_texture_metal', 'wood_texture_metal_trims'],
        colorMeshes: ['wood_texture', 'wood_texture_metal'],
        trimMeshes: ['wood_texture_trims', 'wood_texture_metal_trims'],
        hasTexture: true,
        usesSidingColor: false,
        baseColor: '#FFFFFF',
        textureScale: { u: 0.0075, v: 0.05 } // Vertical wood grain with slight horizontal repeat
      },
      'cedar_shake': {
        standard: ['wood_texture', 'wood_texture_trims'],
        metal: ['wood_texture_metal', 'wood_texture_metal_trims'],
        colorMeshes: ['wood_texture', 'wood_texture_metal'],
        trimMeshes: ['wood_texture_trims', 'wood_texture_metal_trims'],
        hasTexture: true,
        usesSidingColor: false,
        baseColor: '#FFFFFF',
        textureScale: { u: 0.05, v: 0.035 } // Shingle pattern, tiled both directions
      }
    };
    
    // Aliases for different naming conventions
    sidingMeshConfig['cedar-plank'] = sidingMeshConfig['horiz_plank'];
    sidingMeshConfig['cedar-shake'] = sidingMeshConfig['cedar_shake'];
    sidingMeshConfig['cedar'] = sidingMeshConfig['horiz_plank'];

    // All siding meshes (to disable first)
    // NOTE: metal_wainscot is NOT included here - wainscot is an overlay on top of siding
    // Wainscot visibility is managed separately by handleExteriorAddon and reapplyWainscotVisibility
    const allSidingMeshes = [
      'plank_trims', 'plank_trim', 'plank_siding', 'plank_siding_color', 'plank_siding_trims',
      'plank_siding_metal', 'plank_siding_metal_color', 'plank_siding_metal_trims',
      'wood_texture', 'wood_texture_trims', 'wood_texture_metal', 'wood_texture_metal_trims',
      'block_siding', 'block_siding_color', 'block_siding_fastener', 'block_siding_trims',
      'block_siding_metal', 'block_siding_metal_color', 'block_siding_metal_fastener', 'block_siding_metal_trims'
    ];

    // Siding type aliases
    const sidingTypeAliases = {
      'cedar-plank': 'horiz_plank', 'cedar_plank': 'horiz_plank', 'cedarplank': 'horiz_plank', 'cedar': 'horiz_plank',
      'cedar-shake': 'cedar_shake', 'cedarshake': 'cedar_shake',
      'wood': 'lap', 'horizontal': 'lap'
    };
    
    const normalizedType = sidingTypeAliases[actualSidingType] || actualSidingType;
    const finalConfig = sidingMeshConfig[normalizedType] || sidingMeshConfig['lap'];
    
    log('normalizedType:', normalizedType);
    log('finalConfig:', finalConfig);
    
    if (!sidingMeshConfig[normalizedType]) {
      console.warn(`[Signature3D] ⚠️ Unknown siding type: "${actualSidingType}", using 'lap'`);
    }
    
    const meshesToEnable = hasMetalWainscot ? finalConfig.metal : finalConfig.standard;
    const colorMeshPatterns = finalConfig.colorMeshes || [];
    const trimMeshPatterns = finalConfig.trimMeshes || [];
    const fastenerMeshPatterns = finalConfig.fastenerMeshes || [];
    
    // Get colors
    const hexColor = this.getSidingColorHex(actualSidingType, actualColorValue, hexColorOverride);
    const trimColor = this.currentConfig.trimColor || 'white';
    const trimHex = this.getColorHex('trimColor', trimColor);
    const applianceHex = '#C0C0C0';
    
    log('hexColor:', hexColor);
    log('trimHex:', trimHex);
    
    // Get texture URL
    const exteriorStep = this.configData?.steps?.find(s => s.name?.toLowerCase().includes('exterior'));
    const sidingStyleSection = exteriorStep?.sections?.find(sec => sec.id === 'siding');
    log('exteriorStep found:', !!exteriorStep);
    log('sidingStyleSection found:', !!sidingStyleSection);
    
    let styleOption = sidingStyleSection?.options?.find(opt => opt.value === actualSidingType);
    log('styleOption (by actualSidingType):', styleOption);
    
    if (!styleOption && normalizedType !== actualSidingType) {
      styleOption = sidingStyleSection?.options?.find(opt => opt.value === normalizedType);
      log('styleOption (by normalizedType):', styleOption);
    }
    
    // Priority: 1) config textureUrl (e.g., lap ALWAYS uses base_lap.png), 2) textureUrlOverride param, 3) JSON styleOption
    // For lap siding, ALWAYS use base_lap.png regardless of JSON texture
    let textureUrl;
    if (finalConfig.textureUrl) {
      // Config has explicit texture override (e.g., lap siding uses base_lap.png)
      textureUrl = finalConfig.textureUrl;
      log('Using config textureUrl override:', textureUrl);
    } else {
      // Fall back to passed texture or JSON texture
      textureUrl = textureUrlOverride || styleOption?.texture;
    }
    log('===== TEXTURE URL RESOLUTION =====');
    log('textureUrlOverride:', textureUrlOverride);
    log('finalConfig.textureUrl (config override - HIGHEST PRIORITY):', finalConfig.textureUrl);
    log('styleOption?.texture (from JSON):', styleOption?.texture);
    log('Final textureUrl:', textureUrl);
    log('===================================');

    // STEP 1: Disable all siding meshes
    let disabledCount = 0;
    
    this.loadedPanels.forEach((panelData, panelId) => {
      const { model } = panelData;
      model.traverse(child => {
        if (!child.isMesh || !child.name) return;
        const nameLower = child.name.toLowerCase();
        
        const isSidingMesh = allSidingMeshes.some(pattern => nameLower.includes(pattern.toLowerCase()));
        if (isSidingMesh) {
          child.visible = false;
          disabledCount++;
        }
      });
    });

    // STEP 2: Enable appropriate meshes for selected siding + wainscot state
    let enabledCount = 0;
    const colorMeshes = [];
    const trimMeshes = [];
    const fastenerMeshes = [];
    
    log('===== MESH SEARCH & ENABLING =====');
    log('Searching for meshes matching patterns:', meshesToEnable);
    log('Color mesh patterns:', colorMeshPatterns);
    log('Trim mesh patterns:', trimMeshPatterns);
    log('Fastener mesh patterns:', fastenerMeshPatterns);
    
    const foundMeshNames = [];
    this.loadedPanels.forEach((panelData, panelId) => {
      const { model } = panelData;
      model.traverse(child => {
        if (!child.isMesh || !child.name) return;
        const nameLower = child.name.toLowerCase();
        
        // FIX: Exclusive mesh matching to prevent z-fighting between standard and metal meshes
        // Problem: 'wood_texture_metal'.includes('wood_texture') = true, causing both to be enabled
        // Solution: In standard mode, exclude meshes with '_metal'; in metal mode, require '_metal'
        const isMetalMesh = nameLower.includes('_metal');
        let shouldEnable = false;
        
        if (hasMetalWainscot) {
          // Metal mode: only enable meshes that contain '_metal' AND match the pattern
          shouldEnable = isMetalMesh && meshesToEnable.some(pattern => nameLower.includes(pattern.toLowerCase()));
        } else {
          // Standard mode: only enable meshes that do NOT contain '_metal' AND match the pattern
          shouldEnable = !isMetalMesh && meshesToEnable.some(pattern => nameLower.includes(pattern.toLowerCase()));
        }
        
        if (shouldEnable) {
          child.visible = true;
          enabledCount++;
          foundMeshNames.push(child.name);
          
          const isTrimMesh = trimMeshPatterns.some(p => nameLower.includes(p.toLowerCase()));
          const isFastenerMesh = fastenerMeshPatterns.some(p => nameLower.includes(p.toLowerCase()));
          
          if (isTrimMesh) {
            trimMeshes.push(child);
            log('Found trim mesh:', child.name);
          } else if (isFastenerMesh) {
            fastenerMeshes.push(child);
            log('Found fastener mesh:', child.name);
          } else {
            const isColorMesh = colorMeshPatterns.some(p => nameLower.includes(p.toLowerCase()));
            if (isColorMesh) {
              colorMeshes.push(child);
              log('Found color mesh:', child.name);
            } else {
              log('⚠️ Enabled mesh not categorized:', child.name);
            }
          }
        }
      });
    });
    log('Total found mesh names:', foundMeshNames.length);
    log('Sample mesh names:', foundMeshNames.slice(0, 10));
    
    log(`Siding meshes - enabled: ${enabledCount}, color: ${colorMeshes.length}, trim: ${trimMeshes.length}`);
    log('===== MESH ENABLING SUMMARY =====');
    log('enabledCount:', enabledCount);
    log('colorMeshes.length:', colorMeshes.length);
    log('trimMeshes.length:', trimMeshes.length);
    log('fastenerMeshes.length:', fastenerMeshes.length);
    log('meshesToEnable:', meshesToEnable);
    log('colorMeshPatterns:', colorMeshPatterns);
    log('=================================');

    // STEP 3: Apply colors and textures
    log('===== TEXTURE & COLOR APPLICATION =====');
    log('finalConfig.hasTexture:', finalConfig.hasTexture);
    log('textureUrl:', textureUrl);
    log('finalConfig.usesSidingColor:', finalConfig.usesSidingColor);
    log('hexColor:', hexColor);
    
    let sidingTexture = null;
    if (finalConfig.hasTexture && textureUrl) {
      log('===== LOADING TEXTURE =====');
      log('Loading texture URL:', textureUrl);
      log('Expected URL:', 'https://cdn.shopify.com/s/files/1/0969/5304/1190/files/lap-siding.jpg?v=1764337342');
      log('URLs match:', textureUrl === 'https://cdn.shopify.com/s/files/1/0969/5304/1190/files/lap-siding.jpg?v=1764337342');
      
      try {
        sidingTexture = await this.loadTexture(textureUrl);
        if (sidingTexture) {
          log('✓ Texture loaded successfully');
          log('Texture object:', sidingTexture);
          log('Texture image:', sidingTexture.image);
          log('Texture image src:', sidingTexture.image?.src || 'N/A');
          log('Texture image width:', sidingTexture.image?.width || 'N/A');
          log('Texture image height:', sidingTexture.image?.height || 'N/A');
          log('Texture image complete:', sidingTexture.image?.complete || 'N/A');
          
          // IMPORTANT: Set color space for proper color rendering
          sidingTexture.colorSpace = THREE.SRGBColorSpace;
          sidingTexture.wrapS = THREE.RepeatWrapping;
          sidingTexture.wrapT = THREE.RepeatWrapping;
          
          // Adjust texture scale - smaller values = larger/more visible texture
          // For lap siding, we want the texture to be clearly visible
          const scale = finalConfig.textureScale || { u: 4, v: 8 };
          log('Texture scale (u, v):', scale.u, scale.v);
          
          // Set texture repeat - this controls how many times the texture repeats
          // Lower values = texture appears larger/more visible
          sidingTexture.repeat.set(scale.u, scale.v);
          
          // Set texture offset if needed (centers the texture)
          sidingTexture.offset.set(0, 0);
          
          // Ensure texture is updated
          sidingTexture.needsUpdate = true;
          
          log('Texture repeat:', sidingTexture.repeat.x, sidingTexture.repeat.y);
          log('Texture colorSpace:', sidingTexture.colorSpace);
          log('Texture wrapS:', sidingTexture.wrapS);
          log('Texture wrapT:', sidingTexture.wrapT);
          log('=========================');
        } else {
          log('⚠️ Texture loader returned null');
          log('=========================');
        }
      } catch (err) {
        console.warn(`[Signature3D] ❌ Failed to load texture:`, err);
        log('=========================');
      }
    } else {
      log('===== SKIPPING TEXTURE =====');
      if (!finalConfig.hasTexture) {
        log('Skipping texture (hasTexture=false)');
      }
      if (!textureUrl) {
        log('Skipping texture (no textureUrl)');
      }
      log('===========================');
    }
    
    // Determine color: siding color for lap/block/plank, white for cedar types
    const colorToUse = finalConfig.usesSidingColor ? hexColor : (finalConfig.baseColor || '#FFFFFF');
    const sidingColor = new THREE.Color(colorToUse);
    log('colorToUse:', colorToUse);
    log('sidingColor (THREE.Color):', sidingColor);
    log('Applying to', colorMeshes.length, 'color meshes');
    
    // Apply to color meshes
    let textureAppliedCount = 0;
    let colorAppliedCount = 0;
    let materialErrorCount = 0;
    
    log('===== APPLYING MATERIALS TO COLOR MESHES =====');
    log('finalConfig.hasTexture:', finalConfig.hasTexture);
    log('sidingTexture:', sidingTexture ? 'loaded' : 'null');
    log('colorMeshes.length:', colorMeshes.length);
    
    colorMeshes.forEach((mesh, index) => {
      try {
        if (finalConfig.hasTexture && sidingTexture) {
          log(`[${index + 1}/${colorMeshes.length}] Applying texture+color to:`, mesh.name);
          log(`  - Current material:`, mesh.material ? mesh.material.type : 'none');
          log(`  - Texture:`, sidingTexture ? sidingTexture.image?.src || 'loaded' : 'null');
          log(`  - Color:`, sidingColor.getHexString());
          
          // CRITICAL FIX: Don't clone the texture - use it directly
          // Cloning can cause issues with texture updates and visibility
          // Also ensure the material has proper settings for visibility
          log(`  - Creating material with texture URL:`, sidingTexture.image?.src || 'N/A');
          log(`  - Texture image dimensions:`, sidingTexture.image?.width, 'x', sidingTexture.image?.height);
          
          const newMaterial = new THREE.MeshStandardMaterial({
            map: sidingTexture, // Use texture directly, not cloned
            color: sidingColor,
            roughness: 0.8,
            metalness: 0.0,
            side: THREE.DoubleSide,
            // Ensure material is visible
            transparent: false,
            opacity: 1.0,
            // Ensure proper lighting
            emissive: new THREE.Color(0x000000),
            emissiveIntensity: 0
          });
          
          // Ensure texture is properly set on material
          if (newMaterial.map) {
            newMaterial.map.needsUpdate = true;
            log(`  - ✓ Material.map set`);
            log(`  - Material.map.src:`, newMaterial.map.image?.src || 'N/A');
            log(`  - Material.map.repeat:`, newMaterial.map.repeat.x, newMaterial.map.repeat.y);
            log(`  - Material.map.wrapS:`, newMaterial.map.wrapS);
            log(`  - Material.map.wrapT:`, newMaterial.map.wrapT);
            log(`  - Material.map.colorSpace:`, newMaterial.map.colorSpace);
          } else {
            log(`  - ⚠️ Material.map is null!`);
          }
          
          mesh.material = newMaterial;
          mesh.material.needsUpdate = true;
          
          // Force mesh to update
          mesh.visible = true;
          
          // Verify material was set correctly
          log(`  - Mesh material after assignment:`, mesh.material ? mesh.material.type : 'null');
          log(`  - Mesh material.map:`, mesh.material?.map ? 'set' : 'null');
          log(`  - Mesh visible:`, mesh.visible);
          
          textureAppliedCount++;
          
          log(`  - ✓ New material created:`, newMaterial.type);
          log(`  - Material.color:`, newMaterial.color.getHexString());
        } else {
          log(`[${index + 1}/${colorMeshes.length}] Applying color only to:`, mesh.name);
          log(`  - Current material:`, mesh.material ? mesh.material.type : 'none');
          log(`  - Color:`, sidingColor.getHexString());
          
          if (mesh.material) {
            if (Array.isArray(mesh.material)) {
              mesh.material.forEach(m => { 
                if (m.color) {
                  m.color = sidingColor;
                  m.needsUpdate = true;
                }
              });
            } else if (mesh.material.color) {
              mesh.material.color = sidingColor;
              mesh.material.needsUpdate = true;
            }
          } else {
            log(`  - ⚠️ Mesh has no material to update`);
          }
          colorAppliedCount++;
        }
      } catch (error) {
        console.error(`[Signature3D] ❌ Error applying material to mesh ${mesh.name}:`, error);
        materialErrorCount++;
      }
    });
    
    log('Material application summary:');
    log('  - Texture+color applied:', textureAppliedCount);
    log('  - Color only applied:', colorAppliedCount);
    log('  - Errors:', materialErrorCount);
    log('Texture applied to:', textureAppliedCount, 'meshes');
    log('Color applied to:', colorAppliedCount, 'meshes');
    
    // Apply trim color to trim meshes
    log('Applying trim color to', trimMeshes.length, 'trim meshes');
    const trimColorObj = new THREE.Color(trimHex);
    trimMeshes.forEach(mesh => {
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(m => { if (m.color) m.color = trimColorObj; });
        } else if (mesh.material.color) {
          mesh.material.color = trimColorObj;
        }
        mesh.material.needsUpdate = true;
      }
    });
    
    // Apply appliance color to fastener meshes (block siding only)
    log('Applying fastener color to', fastenerMeshes.length, 'fastener meshes');
    const applianceColor = new THREE.Color(applianceHex);
    fastenerMeshes.forEach(mesh => {
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(m => { if (m.color) m.color = applianceColor; });
        } else if (mesh.material.color) {
          mesh.material.color = applianceColor;
        }
        mesh.material.needsUpdate = true;
      }
    });
    
    log('===== RENDERING UPDATE =====');
    // Force render update
    if (this.sceneManager?.renderer) {
      this.sceneManager.renderer.render(this.sceneManager.scene, this.sceneManager.camera);
      log('✓ Renderer updated');
    } else {
      log('⚠️ Renderer not available');
    }
    log('=================================');
  }

  /**
   * Get siding color hex value from config
   */
  getSidingColorHex(sidingType, colorValue, hexOverride = null) {
    // Priority 1: Direct hex override
    if (hexOverride) {
      return hexOverride;
    }
    
    const exteriorStep = this.configData?.steps?.find(s => s.name?.toLowerCase().includes('exterior'));
    const sidingStyleSection = exteriorStep?.sections?.find(sec => sec.id === 'siding');
    const styleOption = sidingStyleSection?.options?.find(opt => opt.value === sidingType);
    
    // Priority 2: Color options nested in siding style
    if (styleOption?.colorOptions?.length) {
      const colorOption = styleOption.colorOptions.find(opt => opt.value === colorValue);
      if (colorOption) {
        return colorOption.color?.hex || colorOption.hex || '#525654';
      }
    }
    
    // Priority 3: Separate sidingColor section
    const sidingColorSection = exteriorStep?.sections?.find(sec => sec.id === 'sidingColor');
    const colorOption = sidingColorSection?.options?.find(opt => opt.value === colorValue);
    if (colorOption) {
      return colorOption.color?.hex || colorOption.hex || '#525654';
    }
    
    return '#525654'; // Default fallback
  }

  /**
   * Load texture helper with CORS support
   */
  async loadTexture(url) {
    log('loadTexture called with URL:', url);
    log('Expected URL:', 'https://cdn.shopify.com/s/files/1/0969/5304/1190/files/lap-siding.jpg?v=1764337342');
    log('URLs match:', url === 'https://cdn.shopify.com/s/files/1/0969/5304/1190/files/lap-siding.jpg?v=1764337342');
    
    return new Promise((resolve, reject) => {
      const loader = new THREE.TextureLoader();
      loader.setCrossOrigin('anonymous'); // Enable CORS for external textures
      loader.load(
        url,
        (texture) => {
          log('✓ TextureLoader success callback');
          log('Loaded texture URL:', url);
          log('Texture object:', texture);
          log('Texture image:', texture.image);
          log('Texture image src:', texture.image?.src || 'N/A');
          log('Texture image naturalWidth:', texture.image?.naturalWidth || 'N/A');
          log('Texture image naturalHeight:', texture.image?.naturalHeight || 'N/A');
          
          // Configure texture properly
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          texture.magFilter = THREE.LinearFilter;
          texture.minFilter = THREE.LinearMipmapLinearFilter;
          texture.generateMipmaps = true;
          texture.needsUpdate = true;
          
          log(`Texture loaded: ${url.split('/').pop()}`);
          log('Texture configured and ready');
          resolve(texture);
        },
        (progress) => {
          // Progress callback (optional)
          if (progress && progress.total) {
            const percent = (progress.loaded / progress.total) * 100;
            log(`Texture loading progress: ${percent.toFixed(1)}%`);
          }
        },
        (error) => {
          console.error('[Signature3D] ❌ Texture load error:', url, error);
          console.error('[Signature3D] Error details:', error);
          resolve(null);
        }
      );
    });
  }

  /**
   * Apply solid color to a material group
   */
  applyColorToGroup(groupId, colorValue) {
    log(`═══ APPLYING COLOR: ${groupId} = ${colorValue} ═══`);
    
    // Default mesh patterns for common material groups
    // Note: Fascia = vertical edge board, Soffit = underside of eaves
    // Model uses: eave_rafter for rafters/fascia, eave_plywood for soffit (underside panel)
    const defaultMeshPatterns = {
      trim: ['trim_color', 'trim'],
      door_color: ['door_color', 'door'],
      fascia: ['fascia', 'fascia_color', 'fascia_board', 'eave_rafter'],
      soffit: ['soffit', 'soffit_color', 'soffit_panel', 'eave_soffit', 'roof_soffit', 'eave_plywood'],
      eaves: ['eaves_color', 'eaves', 'eave_color', 'eave_plywood', 'eave_rafter', 'eave_soffit'],
      glass: ['glass'],
      roof_material: ['roof_material', 'shingle']
    };
    
    const groupConfig = this.materialGroups[groupId];
    const meshPatterns = groupConfig?.meshNames || defaultMeshPatterns[groupId] || [groupId];
    
    log(`  Mesh patterns:`, meshPatterns);

    // Find the color option
    const section = this.findSectionDataByKey(groupId);
    const colorOption = section?.options?.find(o => o.value === colorValue);
    const hexColor = colorOption?.color?.hex || colorOption?.hex || groupConfig?.defaultColor;
    
    log(`  Hex color: ${hexColor}`);

    // Handle special case for no color
    if (!hexColor) {
      log(`  ⚠️ Skipping - no hex color`);
      return;
    }

    // Find meshes by patterns
    let meshes = [];
    this.loadedPanels.forEach((panelData, panelId) => {
      const { model } = panelData;
      model.traverse(child => {
        if (child.isMesh) {
          const name = (child.name || '').toLowerCase();
          if (meshPatterns.some(pattern => name.includes(pattern.toLowerCase()))) {
            meshes.push(child);
            log(`  ✓ Found mesh: "${child.name}"`);
          }
        }
      });
    });

    if (!meshes.length) {
      log(`  ❌ No meshes found! Listing all mesh names in roof panel...`);
      this.loadedPanels.forEach((panelData, panelId) => {
        if (panelId.toLowerCase().includes('roof')) {
          log(`  ROOF panel meshes:`);
          panelData.model.traverse(child => {
            if (child.isMesh && child.name) {
              log(`    - ${child.name}`);
            }
          });
        }
      });
      return;
    }
    
    log(`  Found ${meshes.length} meshes`);

    // Create and apply the material
    const material = new THREE.MeshStandardMaterial({
      color: new THREE.Color(hexColor),
      roughness: groupConfig?.type === 'solidColor' ? 0.4 : 0.7,
      metalness: groupId === 'trim' ? 0.3 : 0.0,
      side: THREE.DoubleSide
    });

    meshes.forEach(mesh => {
      if (mesh.material && mesh.material.dispose) {
        mesh.material.dispose();
      }
      mesh.material = material.clone();
      mesh.material.needsUpdate = true;
    });
    
    log(`Applied ${hexColor} to ${meshes.length} meshes for ${groupId}`);
  }

  /**
   * Apply floor texture to ground plane and any floor meshes
   */
  async applyFloorFinish(finishValue) {
    log(`Applying floor finish: ${finishValue}`);
    const groupConfig = this.materialGroups.floor;

    const section = this.findSectionData('mainFloorFinish');
    const option = section?.options?.find(o => o.value === finishValue);
    const textureUrl = option?.texture || groupConfig?.defaultTexture;
    log(`Floor texture URL: ${textureUrl}`);

    if (!textureUrl) {
      log('No floor texture URL');
      return;
    }

    try {
      // Get texture scale - convert from JSON format (u/v) to MaterialManager format (uScale/vScale)
      const jsonScale = groupConfig?.textureScale || { u: 4, v: 4 };
      const textureScale = { 
        uScale: jsonScale.uScale || jsonScale.u || 4, 
        vScale: jsonScale.vScale || jsonScale.v || 4 
      };
      
      // Create the floor material with texture
      const material = await this.materialManager.createTexturedMaterial(textureUrl, {
        textureScale,
        roughness: 0.7,
        metalness: 0.0
      });

      // Apply to the ground plane (SceneManager's ground)
      const ground = this.sceneManager.getGround();
      if (ground) {
        this.sceneManager.setGroundMaterial(material.clone());
        // Only show floor if NOT shell-only
        const interiorPackage = this.currentConfig?.interiorPackage || 
                               this.configData?.defaultConfiguration?.interiorPackage || 
                               'shell-only';
        ground.visible = (interiorPackage !== 'shell-only');
        log(`Floor finish applied to ground plane, visible: ${ground.visible}`);
      }

      // Also apply to any floor meshes in the model
      if (groupConfig?.meshNames) {
        const meshes = this.findMeshesByNames(groupConfig.meshNames);
        if (meshes.length) {
      meshes.forEach(mesh => { mesh.material = material.clone(); });
          log(`Floor finish applied to ${meshes.length} model meshes`);
        }
      }
      
      log(`Floor finish "${finishValue}" applied successfully`);
    } catch (error) {
      console.error('Failed to apply floor finish:', error);
    }
  }

  /**
   * Apply glass material
   */
  applyGlassMaterial() {
    const groupConfig = this.materialGroups.glass;
    if (!groupConfig) return;

    const meshes = this.findMeshesByNames(groupConfig.meshNames);
    if (!meshes.length) return;

    // Create glass material directly (MaterialManager.applyGlassMaterial expects group names, not meshes)
    const glassMaterial = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(groupConfig.defaultColor || '#87CEEB'),
      transparent: true,
      opacity: groupConfig.opacity || 0.3,
      roughness: 0.0,
      metalness: 0.0,
      transmission: 0.9,
      thickness: 0.1,
      side: THREE.DoubleSide
    });

    meshes.forEach(mesh => {
      mesh.material = glassMaterial.clone();
    });
  }

  /**
   * Find section data by ID
   */
  findSectionData(sectionId) {
    if (!this.configData?.steps) return null;
    for (const step of this.configData.steps) {
      const section = step.sections?.find(s => s.id === sectionId);
      if (section) return section;
    }
    return null;
  }

  /**
   * Find section data by material group key
   */
  findSectionDataByKey(key) {
    // Map group keys to section IDs
    const keyToSectionMap = {
      'trim': 'trimColor',
      'door_color': 'doorColor',
      'fascia': 'fasciaColor',
      'soffit': 'soffitColor',
      'eaves': 'eavesColor'
    };
    const sectionId = keyToSectionMap[key] || key;
    return this.findSectionData(sectionId);
  }

  /**
   * Find meshes by name patterns across all loaded panels
   */
  findMeshesByNames(namePatterns) {
    const meshes = [];
    
    // Validate and normalize namePatterns to an array
    if (!namePatterns) return meshes;
    
    let patterns = namePatterns;
    if (typeof namePatterns === 'string') {
      patterns = [namePatterns];
    } else if (!Array.isArray(namePatterns)) {
      console.warn('findMeshesByNames: Invalid namePatterns provided:', namePatterns);
      return meshes;
    }
    
    this.loadedPanels.forEach(({ model }) => {
      model.traverse(child => {
        if (!child.isMesh) return;
        const meshName = (child.name || '').toLowerCase();
        
        for (const pattern of patterns) {
          if (typeof pattern === 'string' && meshName.includes(pattern.toLowerCase())) {
            meshes.push(child);
            break;
          }
        }
      });
    });

    return meshes;
  }

  /**
   * Update configuration for a single option
   */
  updateConfiguration(config) {
    if (!this.isInitialized) {
      log('⚠️ updateConfiguration - not initialized');
      return;
    }

    log('===== updateConfiguration =====');
    log('Full config object:', JSON.stringify(config, null, 2));
    log('config.section:', config.section);
    log('config.value:', config.value);
    log('config.texture:', config.texture);
    log('config.hex:', config.hex);
    log('this.currentConfig:', JSON.stringify(this.currentConfig));

    log('updateConfiguration called:', config);

    const { section, value, modelFile, hex, texture, meshGroups } = config;

    // Check if this is a panel change (requires model reload)
    // Support multiple naming formats: frontPanel, front-panel, front_panel, etc.
    const sectionLower = section.toLowerCase().replace(/[-_\s]/g, '');
    const isPanelChange = ['frontpanel', 'backpanel', 'leftpanel', 'rightpanel', 'roof'].includes(sectionLower);
    
    const skipFocus = config.skipFocus === true;
    log('isPanelChange:', isPanelChange, 'section:', section);
    
    if (isPanelChange) {
      // Rotate camera to show the selected panel side (but not during initial load)
      if (!skipFocus) {
        log('User clicked panel - rotating camera');
        this.setViewForSection(section);
      } else {
        log('Skipping camera rotation (initial load)');
      }
      
      // If there's a model file change, update the panel
      if (modelFile) {
        log('Updating panel:', section);
      this.updatePanel(section, config);
      }
      return;
    }

    // Handle material changes
    const handlers = {
      // Siding STYLE change - applySidingMaterial handles metal wainscot internally
      siding: () => {
        log('===== SIDING HANDLER CALLED =====');
        log('value (siding type):', value);
        log('this.currentConfig.sidingColor:', this.currentConfig.sidingColor);
        log('texture:', texture);
        log('Calling applySidingMaterial...');
        this.applySidingMaterial(value, this.currentConfig.sidingColor, texture, null);
        // CRITICAL: Re-apply wainscot visibility after siding change
        if (this.currentConfig.exteriorAddon === 'metal-wainscot') {
          this.reapplyWainscotVisibility();
        }
      },
      // Siding COLOR change - pass hex directly for dynamic colors
      sidingColor: () => {
        log('===== SIDING COLOR HANDLER CALLED =====');
        log('this.currentConfig.siding:', this.currentConfig.siding);
        log('value (color):', value);
        log('hex:', hex);
        this.applySidingMaterial(this.currentConfig.siding, value, null, hex);
        // CRITICAL: Re-apply wainscot visibility after siding color change
        if (this.currentConfig.exteriorAddon === 'metal-wainscot') {
          this.reapplyWainscotVisibility();
        }
      },
      trimColor: () => this.applyTrimColor(value),
      doorColor: () => this.applyColorToGroup('door_color', value),
      fasciaColor: () => this.applyColorToGroup('fascia', value),
      soffitColor: () => this.applyColorToGroup('soffit', value),
      eavesColor: () => this.applyColorToGroup('eaves', value),
      mainFloorFinish: () => this.applyFloorFinish(value),
      interiorPackage: () => this.handleInteriorPackageChange(value),
      // Addon handlers - show/hide meshes based on selection
      exteriorAddon: () => this.handleAddonChange('exterior', value),
      roofAddon: () => this.handleAddonChange('roof', value)
    };

    const handler = handlers[section];
    if (handler) {
      log('Handler found for section:', section);
      log('Updating this.currentConfig[' + section + '] from', this.currentConfig[section], 'to', value);
      this.currentConfig[section] = value;
      log('this.currentConfig[' + section + '] after update:', this.currentConfig[section]);
      handler();
    } else {
      log('⚠️ No handler found for section:', section);
      if (hex && meshGroups?.length) {
        log('Using generic color application');
        // Generic color application (inline to avoid dependency on new MaterialManager method)
        const meshes = this.findMeshesByGroupNames(meshGroups);
        const color = new THREE.Color(hex);
        meshes.forEach(mesh => {
          if (mesh.material) {
            if (Array.isArray(mesh.material)) {
              mesh.material.forEach(m => { if (m.color) m.color = color; });
            } else if (mesh.material.color) {
              mesh.material.color = color;
            }
          }
        });
      } else if (texture && meshGroups?.length) {
        log('Using generic texture application');
        // Generic texture application
        this.applyTextureToGroups(meshGroups, texture);
      } else {
        log('⚠️ No handler and no hex/texture/meshGroups available');
      }
    }

    this.onConfigChange({ key: section, value });
    log('=================================');
  }

  /**
   * Update a specific panel with new model
   */
  async updatePanel(panelId, config) {
    if (this.isLoading) return;

    this.isLoading = true;
    this.container.dispatchEvent(new CustomEvent('configurator:modelLoading', {
      detail: { panel: panelId }
    }));

    try {
      // Remove existing panel
      const existing = this.loadedPanels.get(panelId);
      if (existing) {
        // Use scene.remove directly (SceneManager doesn't have removeFromScene method)
        this.sceneManager.scene.remove(existing.model);
        // Use inline disposal method (avoid modifying shared ModelLoader)
        this.disposeModelInline(existing.model);
        this.loadedPanels.delete(panelId);
      }

      // Find the loading sequence item for position/rotation
      const sequenceItem = this.loadingSequence.find(item => item.id === panelId);
      if (!sequenceItem) {
        throw new Error(`No sequence item found for panel ${panelId}`);
      }

      // Update current config
      this.currentConfig[panelId] = config.value;

      // Load new panel
      await this.loadSequenceItem(sequenceItem, this.currentConfig);

      // Reapply materials to new panel
      await this.applyMaterials(this.currentConfig);

      this.container.dispatchEvent(new CustomEvent('configurator:modelLoaded', {
        detail: { panel: panelId }
      }));
    } catch (error) {
      console.error(`Failed to update panel ${panelId}:`, error);
      this.dispatchError('panelUpdate', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handle interior package change - show/hide floor based on package
   */
  handleInteriorPackageChange(value) {
    this.currentConfig.interiorPackage = value;
    log('Interior package changed to:', value);

    const isShellOnly = value === 'shell-only';

    // Toggle lifestyle mesh group (mirrors Bliss setInteriorPackage → Harp.lifestyle.setEnabled)
    this.modelLoader.setGroupVisibility('lifestyle', !isShellOnly);

    if (isShellOnly) {
      log('Shell Only selected - swapping floor to concrete subfloor');
      this.showFloor();
      const concreteUrl = this.configData?.textures?.concrete ||
                         'https://cdn.shopify.com/s/files/1/0969/5304/1190/files/concrete.jpg?v=1764337343';
      this.materialManager.applyTextureToGroups(
        ['main_floor', 'flooring', 'floor'],
        concreteUrl,
        { textureScale: { uScale: 1, vScale: 1 } }
      );
      if (this.sceneManager?.setGroundMaterial) {
        this.materialManager.createTexturedMaterial(concreteUrl, {
          roughness: 0.9, metalness: 0.0,
          textureScale: { uScale: 1, vScale: 1 }
        }).then(mat => this.sceneManager.setGroundMaterial(mat));
      }
    } else {
      this.showFloor();
      const mainFloor = this.currentConfig.mainFloorFinish ||
                       this.configData?.defaultConfiguration?.mainFloorFinish ||
                       'ashlar-oak';
      this.applyFloorFinish(mainFloor);
    }
  }

  /**
   * Handle addon changes (exteriorAddon, roofAddon)
   */
  handleAddonChange(addonType, value) {
    log(`Addon change: ${addonType} -> ${value}`);
    this.currentConfig[`${addonType}Addon`] = value;

    if (addonType === 'exterior') {
      this.handleExteriorAddon(value);
    } else if (addonType === 'roof') {
      this.handleRoofAddon(value);
    }
  }

  /**
   * Handle exterior addon (metal wainscot)
   * Shows/hides wainscot meshes and refreshes siding for standard/metal mesh sets
   * Updated to match Summit model behavior for proper wainscot rendering
   */
  handleExteriorAddon(value) {
    const isMetalWainscot = value === 'metal-wainscot';
    
    // Comprehensive wainscot mesh patterns (matching Summit model)
    const wainscotMeshPatterns = [
      'metal_wainscot', 'metal_wainscot1', 'metal_wainscot2', 'metal_wainscot3', 'metal_wainscot4',
      'wainscot', 'wainscot1', 'wainscot2', 'wainscot3', 'wainscot4',
      'wainscot_front', 'wainscot_back', 'wainscot_left', 'wainscot_right',
      'metal_wainscot_front', 'metal_wainscot_back', 'metal_wainscot_left', 'metal_wainscot_right'
    ];
    
    const trimColor = this.currentConfig.trimColor || 'white';
    const trimHex = this.getColorHex('trimColor', trimColor);

    log(`Exterior addon: ${value}, wainscot: ${isMetalWainscot}, trimColor: ${trimColor}`);
    log('Using wainscot mesh patterns:', wainscotMeshPatterns);

    let toggledCount = 0;
    const toggledMeshNames = [];

    // Toggle wainscot meshes and apply trim color
    this.loadedPanels.forEach((panelData, panelId) => {
      const { model } = panelData;
      model.traverse(child => {
        if (!child.isMesh || !child.name) return;
        const nameLower = child.name.toLowerCase();
        const isWainscot = wainscotMeshPatterns.some(pattern => nameLower.includes(pattern.toLowerCase()));
        
        if (isWainscot) {
          child.visible = isMetalWainscot;
          
          // CRITICAL: Set renderOrder so wainscot renders ON TOP of siding meshes
          if (isMetalWainscot) {
            child.renderOrder = 1; // Render after siding (which defaults to 0)
            
            // Ensure proper depth testing for correct rendering
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(m => {
                  if (m.depthTest !== undefined) m.depthTest = true;
                  if (m.depthWrite !== undefined) m.depthWrite = true;
                });
              } else {
                if (child.material.depthTest !== undefined) child.material.depthTest = true;
                if (child.material.depthWrite !== undefined) child.material.depthWrite = true;
              }
            }
          } else {
            // Reset renderOrder when hiding wainscot
            child.renderOrder = 0;
          }
          
          // CRITICAL: Ensure parent objects are also visible
          if (isMetalWainscot) {
            let parent = child.parent;
            while (parent && parent !== this.sceneManager?.scene) {
              if (parent.visible === false) {
                log(`Making parent visible for ${child.name}:`, parent.name || parent.type || 'unnamed');
                parent.visible = true;
              }
              parent = parent.parent;
            }
          }
          
          toggledCount++;
          toggledMeshNames.push(child.name);
          
          // Apply trim color with metallic properties to visible wainscot meshes
          // CRITICAL: Create a NEW MeshStandardMaterial to ensure metallic properties work
          // The OBJ loader may have assigned MeshBasicMaterial which doesn't support metalness
          if (isMetalWainscot && trimHex) {
            const color = new THREE.Color(trimHex);
            
            // Create proper metallic material for wainscot (specularPower: 4 equivalent)
            const wainscotMaterial = new THREE.MeshStandardMaterial({
              color: color,
              roughness: 0.25,      // Low roughness for metallic sheen
              metalness: 0.7,       // High metalness for metallic appearance
              side: THREE.DoubleSide,
              envMapIntensity: 1.0  // Environment map reflection
            });
            
            // Dispose old material if exists
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(m => { if (m.dispose) m.dispose(); });
              } else {
                if (child.material.dispose) child.material.dispose();
              }
            }
            
            // Apply new metallic material
            child.material = wainscotMaterial;
            child.material.needsUpdate = true;
            
            log('Applied metallic material to wainscot:', child.name, 
              'color:', color.getHexString(), 'roughness:', 0.25, 'metalness:', 0.7);
          }
        }
      });
    });

    log(`Toggled ${toggledCount} wainscot meshes:`, toggledMeshNames);

    // Refresh siding to use correct mesh set (standard vs metal)
    const currentSiding = this.currentConfig.siding || 'lap';
    const currentColor = this.currentConfig.sidingColor || 'iron-gray';
    this.applySidingMaterial(currentSiding, currentColor);
    
    // CRITICAL: Re-apply wainscot visibility AFTER siding refresh
    // The siding refresh may have hidden/modified the wainscot meshes
    if (isMetalWainscot) {
      this.reapplyWainscotVisibility();
    }
  }
  
  /**
   * Re-apply wainscot visibility and properties
   * Called after siding operations to ensure wainscot remains visible
   * Matches Summit model behavior
   */
  reapplyWainscotVisibility() {
    const hasMetalWainscot = this.currentConfig?.exteriorAddon === 'metal-wainscot';
    
    if (!hasMetalWainscot) {
      return; // Wainscot not enabled, nothing to do
    }
    
    log('Re-applying wainscot visibility...');
    
    const wainscotMeshPatterns = [
      'metal_wainscot', 'metal_wainscot1', 'metal_wainscot2', 'metal_wainscot3', 'metal_wainscot4',
      'wainscot', 'wainscot1', 'wainscot2', 'wainscot3', 'wainscot4',
      'wainscot_front', 'wainscot_back', 'wainscot_left', 'wainscot_right',
      'metal_wainscot_front', 'metal_wainscot_back', 'metal_wainscot_left', 'metal_wainscot_right'
    ];
    
    const trimColor = this.currentConfig.trimColor || 'white';
    const trimHex = this.getColorHex('trimColor', trimColor);
    
    let reAppliedCount = 0;
    
    this.loadedPanels.forEach((panelData, panelId) => {
      const { model } = panelData;
      model.traverse(child => {
        if (!child.isMesh || !child.name) return;
        const nameLower = child.name.toLowerCase();
        const isWainscot = wainscotMeshPatterns.some(pattern => nameLower.includes(pattern.toLowerCase()));
        
        if (isWainscot) {
          // Ensure mesh is visible
          if (!child.visible) {
            child.visible = true;
            log(`Re-enabling wainscot mesh: ${child.name}`);
          }
          
          // Set render order to render on top of siding
          child.renderOrder = 1;
          
          // Ensure parent objects are visible
          let parent = child.parent;
          while (parent && parent !== this.sceneManager?.scene) {
            if (parent.visible === false) {
              parent.visible = true;
            }
            parent = parent.parent;
          }
          
          // Re-apply metallic material with trim color
          if (trimHex) {
            const color = new THREE.Color(trimHex);
            const wainscotMaterial = new THREE.MeshStandardMaterial({
              color: color,
              roughness: 0.25,
              metalness: 0.7,
              side: THREE.DoubleSide,
              envMapIntensity: 1.0
            });
            
            if (child.material && child.material.dispose) {
              child.material.dispose();
            }
            child.material = wainscotMaterial;
            child.material.needsUpdate = true;
          }
          
          reAppliedCount++;
        }
      });
    });
    
    log(`Re-applied visibility to ${reAppliedCount} wainscot meshes`);
  }

  /**
   * Hide metal wainscot-related meshes on initial load
   */
  hideMetalWainscotMeshes(shouldHide = true) {
    // Comprehensive patterns for all metal/wainscot meshes (matching Summit model)
    const metalMeshPatterns = [
      // Wainscot meshes
      'metal_wainscot', 'metal_wainscot1', 'metal_wainscot2', 'metal_wainscot3', 'metal_wainscot4',
      'wainscot', 'wainscot1', 'wainscot2', 'wainscot3', 'wainscot4',
      'wainscot_front', 'wainscot_back', 'wainscot_left', 'wainscot_right',
      'metal_wainscot_front', 'metal_wainscot_back', 'metal_wainscot_left', 'metal_wainscot_right',
      // Metal siding meshes (upper siding when wainscot is enabled)
      'block_siding_metal', 'block_siding_metal_color', 'block_siding_metal_fastener', 'block_siding_metal_trims',
      'plank_siding_metal', 'plank_siding_metal_color', 'plank_siding_metal_trims',
      'wood_texture_metal', 'wood_texture_metal_trims'
    ];
    
    this.loadedPanels.forEach((panelData, panelId) => {
      const { model } = panelData;
      model.traverse(child => {
        if (!child.isMesh || !child.name) return;
        const nameLower = child.name.toLowerCase();
        const isMetalMesh = metalMeshPatterns.some(pattern => nameLower.includes(pattern.toLowerCase()));
        if (isMetalMesh) {
          child.visible = !shouldHide;
        }
      });
    });
  }

  /**
   * Apply trim color to ALL affected mesh groups
   * Matches blissData.js specification - trim color affects:
   * 1. General trim (trim_color)
   * 2. Plank siding trims (plank_trim, plank_trims, plank_siding_trims, plank_siding_metal_trims)
   * 3. Wood texture trims (wood_texture_trims, wood_texture_metal_trims)
   * 4. Block siding trims (block_siding_trims, block_siding_metal_trims)
   * 5. Roof metal (roof_metal)
   * 6. Metal wainscot (metal_wainscot, metal_wainscot1)
   * 7. Window exterior (conditional mapping)
   */
  applyTrimColor(colorValue) {
    log('===== APPLYING TRIM COLOR =====');
    log('Trim color value:', colorValue);
    
    const trimHex = this.getColorHex('trimColor', colorValue);
    log('Trim hex color:', trimHex);
    
    if (!trimHex) {
      console.warn('[Signature3D] No hex color found for trim:', colorValue);
      return;
    }
    
    // All mesh groups affected by trim color (from blissData.js spec)
    const trimAffectedGroups = {
      general: ['trim_color'],
      plankSiding: ['plank_trim', 'plank_trims', 'plank_siding_trims', 'plank_siding_metal_trims'],
      woodTexture: ['wood_texture_trims', 'wood_texture_metal_trims'],
      blockSiding: ['block_siding_trims', 'block_siding_metal_trims'],
      roof: ['roof_metal'],
      metalWainscot: ['metal_wainscot', 'metal_wainscot1', 'metal_wainscot2', 'metal_wainscot3', 'metal_wainscot4', 'wainscot', 'wainscot1', 'wainscot2', 'wainscot3', 'wainscot4', 'wainscot_front', 'wainscot_back', 'wainscot_left', 'wainscot_right']
    };
    
    // Window casement color — derived from trim color selection (matches Summit)
    const WINDOW_CASEMENT_MAP = {
      'aluminum':           '#8C826E',  // Pebble Gray — warm gray for CA trim
      'studio-shed-bronze': '#202021',  // Ebony — near-black for bronze trim
    };
    
    // Flatten all mesh patterns
    const allTrimPatterns = Object.values(trimAffectedGroups).flat();
    log('Applying trim color to patterns:', allTrimPatterns);
    
    // Create trim material with metallic properties for metal elements
    const trimColor = new THREE.Color(trimHex);
    const standardMaterial = new THREE.MeshStandardMaterial({
      color: trimColor,
      roughness: 0.4,
      metalness: 0.3,
      side: THREE.DoubleSide
    });
    
    // Metallic material for metal wainscot and roof metal
    // Using low roughness (0.25) and high metalness (0.7) for metallic sheen (specularPower: 4 equivalent)
    const metallicMaterial = new THREE.MeshStandardMaterial({
      color: trimColor,
      roughness: 0.25,
      metalness: 0.7,
      side: THREE.DoubleSide,
      envMapIntensity: 1.0
    });
    
    let appliedCount = 0;
    
    // Apply to all mesh groups
    this.loadedPanels.forEach((panelData, panelId) => {
      const { model } = panelData;
      model.traverse(child => {
        if (!child.isMesh || !child.name) return;
        const nameLower = child.name.toLowerCase();
        
        // Check if mesh matches any trim pattern
        const matchesPattern = allTrimPatterns.some(pattern => 
          nameLower.includes(pattern.toLowerCase())
        );
        
        if (matchesPattern) {
          // Use metallic material for metal wainscot and roof metal
          const isMetalElement = nameLower.includes('metal_wainscot') || 
                                 nameLower.includes('roof_metal');
          
          const material = isMetalElement ? metallicMaterial.clone() : standardMaterial.clone();
          
          if (child.material && child.material.dispose) {
            child.material.dispose();
          }
          child.material = material;
          child.material.needsUpdate = true;
          appliedCount++;
          log('Applied trim color to:', child.name, isMetalElement ? '(metallic)' : '(standard)');
        }
      });
    });
    
    log('Total meshes with trim color applied:', appliedCount);
    
    // Apply window casement color (derived from trim)
    const casementHex = WINDOW_CASEMENT_MAP[colorValue];
    if (casementHex) {
      log('Applying window casement color:', colorValue, '->', casementHex);
      const windowColor = new THREE.Color(casementHex);
      const windowMaterial = new THREE.MeshStandardMaterial({
        color: windowColor,
        roughness: 0.45,
        metalness: 0.35,
        side: THREE.DoubleSide
      });

      this.loadedPanels.forEach((panelData) => {
        const { model } = panelData;
        model.traverse(child => {
          if (!child.isMesh || !child.name) return;
          if (child.name.toLowerCase().includes('window_exterior')) {
            if (child.material && child.material.dispose) child.material.dispose();
            child.material = windowMaterial.clone();
            child.material.needsUpdate = true;
            log('Applied window casement color to:', child.name);
          }
        });
      });
    }
    
    log('===== TRIM COLOR COMPLETE =====');
  }

  /**
   * Handle roof addon (e.g., shortened back eaves)
   */
  handleRoofAddon(value) {
    log(`Roof addon: ${value}`);
    
    // Define roof addon mesh patterns
    const roofAddonMeshes = {
      'none': [], // Standard roof - no special meshes
      'b6': ['eave_back_6', 'back_eave_6', 'shortened_eave', 'b6_eave']
    };

    // All roof addon meshes (to manage visibility)
    const allRoofAddonMeshes = ['eave_back_6', 'back_eave_6', 'shortened_eave', 'b6_eave'];
    const activePatterns = roofAddonMeshes[value] || [];

    let shownCount = 0;
    let hiddenCount = 0;

    this.loadedPanels.forEach((panelData, panelId) => {
      const { model } = panelData;
      model.traverse(child => {
        if (!child.isMesh || !child.name) return;
        const nameLower = child.name.toLowerCase();

        const isRoofAddonMesh = allRoofAddonMeshes.some(p => nameLower.includes(p.toLowerCase()));
        
        if (isRoofAddonMesh) {
          const shouldShow = activePatterns.some(p => nameLower.includes(p.toLowerCase()));
          child.visible = shouldShow;
          if (shouldShow) shownCount++;
          else hiddenCount++;
        }
      });
    });

    log(`Roof addon: ${shownCount} shown, ${hiddenCount} hidden`);
  }

  /**
   * Get hex color for a color section and value
   */
  getColorHex(sectionId, colorValue) {
    const exteriorStep = this.configData?.steps?.find(s => s.name?.toLowerCase().includes('exterior'));
    const section = exteriorStep?.sections?.find(sec => sec.id === sectionId);
    const colorOption = section?.options?.find(opt => opt.value === colorValue);
    return colorOption?.color?.hex || colorOption?.hex || '#FFFFFF';
  }

  /**
   * Log all mesh names in loaded panels (for debugging)
   */
  logAllMeshNames() {
    if (!DEBUG) return;
    log('All available meshes in model:');
    this.loadedPanels.forEach((panelData, panelId) => {
      const { model } = panelData;
      model.traverse(child => {
        if (child.isMesh && child.name) {
          log(`  ${panelId}: ${child.name}`);
        }
      });
    });
  }

  /**
   * Hide the floor/ground plane (for Shell Only package)
   */
  hideFloor() {
    if (this.sceneManager.ground) {
      this.sceneManager.ground.visible = false;
    }
    
    const floorConfig = this.materialGroups?.floor;
    if (floorConfig?.meshNames) {
      const meshes = this.findMeshesByNames(floorConfig.meshNames);
      meshes.forEach(mesh => {
        mesh.visible = false;
      });
    }
    log('Floor hidden');
  }

  /**
   * Show the floor/ground plane
   */
  showFloor() {
    if (this.sceneManager.ground) {
      this.sceneManager.ground.visible = true;
    }
    
    const floorConfig = this.materialGroups?.floor;
    if (floorConfig?.meshNames) {
      const meshes = this.findMeshesByNames(floorConfig.meshNames);
      meshes.forEach(mesh => {
        mesh.visible = true;
      });
    }
    log('Floor shown');
  }

  /**
   * Find meshes by mesh group names
   */
  findMeshesByGroupNames(groupNames) {
    const meshes = [];
    
    groupNames.forEach(groupName => {
      const groupConfig = this.materialGroups[groupName];
      if (groupConfig?.meshNames) {
        meshes.push(...this.findMeshesByNames(groupConfig.meshNames));
      }
    });

    return meshes;
  }

  /**
   * Apply texture to groups
   */
  async applyTextureToGroups(groupNames, textureUrl) {
    const meshes = this.findMeshesByGroupNames(groupNames);
    if (!meshes.length) return;

    try {
      const material = await this.materialManager.createTexturedMaterial(textureUrl);
      meshes.forEach(mesh => { mesh.material = material.clone(); });
    } catch (error) {
      console.error('Failed to apply texture to groups:', error);
    }
  }

  /**
   * Set camera view for step
   * Step 1 "Choose Your Interior" - show top-down view with roof hidden
   * Other steps - show exterior front view with roof visible
   */
  setViewForStep(step) {
    this.currentStep = step;
    const roofPanel = this.loadedPanels.get('roof');
    
    if (step === 1) {
      // Step 1: Interior view - hide roof, top-down camera to see inside
      if (roofPanel?.model) {
        roofPanel.model.visible = false;
      }
      this.cameraControls?.setTopDownView();
      log(`Step ${step}: Interior view (roof hidden, top-down camera)`);
    } else {
      // All other steps: Exterior view - show roof, front angle camera
      if (roofPanel?.model) {
        roofPanel.model.visible = true;
      }
      this.cameraControls?.setFrontView();
      log(`Step ${step}: Exterior view (roof visible, front camera)`);
    }
  }

  /**
   * Toggle interior view (hide roof, show top-down camera)
   */
  showInteriorView() {
    const roofPanel = this.loadedPanels.get('roof');
    if (roofPanel) {
      roofPanel.model.visible = false;
    }
      this.cameraControls?.setTopDownView();
  }

  /**
   * Toggle exterior view (show roof, show front angle camera)
   */
  showExteriorView() {
    const roofPanel = this.loadedPanels.get('roof');
    if (roofPanel) {
      roofPanel.model.visible = true;
    }
      this.cameraControls?.setFrontView();
    }

  /**
   * Set camera view based on panel section being configured (Signature model specific)
   * @param {string} sectionId - The section ID (e.g., 'frontPanel', 'backPanel', 'leftPanel', 'rightPanel')
   */
  /**
   * Set camera view based on panel section (Signature model specific)
   */
  setViewForSection(sectionId) {
    if (!this.cameraControls) return;
    
    const sectionLower = sectionId.toLowerCase();
    
    try {
      if (sectionLower.includes('front')) {
        if (typeof this.cameraControls.setFrontPanelView === 'function') {
          this.cameraControls.setFrontPanelView();
        } else {
          this.cameraControls.rotateTo(0);
        }
      } else if (sectionLower.includes('back')) {
        if (typeof this.cameraControls.setBackPanelView === 'function') {
          this.cameraControls.setBackPanelView();
        } else {
          this.cameraControls.rotateTo(180);
        }
      } else if (sectionLower.includes('left')) {
        if (typeof this.cameraControls.setLeftPanelView === 'function') {
          this.cameraControls.setLeftPanelView();
        } else {
          this.cameraControls.rotateTo(270);
        }
      } else if (sectionLower.includes('right')) {
        if (typeof this.cameraControls.setRightPanelView === 'function') {
          this.cameraControls.setRightPanelView();
        } else {
          this.cameraControls.rotateTo(90);
        }
      }
    } catch (error) {
      console.warn('[Signature3D] Failed to set view for section:', sectionId, error);
    }
  }

  /**
   * Event dispatching
   */
  onConfigChange(data) {
    this.container.dispatchEvent(new CustomEvent('configurator:change', { detail: data }));
  }

  onModelLoaded() {
    this.container.dispatchEvent(new CustomEvent('configurator:modelLoaded', {
      detail: { panels: Array.from(this.loadedPanels.keys()) }
    }));
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  resetCamera() {
    this.cameraControls?.reset();
  }

  rotateCamera(degrees) {
    this.cameraControls?.rotateBy(degrees);
  }

  setInteriorView(enabled) {
    if (enabled) {
      this.showInteriorView();
    } else {
      this.showExteriorView();
    }
  }

  getState() {
    return {
      step: this.currentStep,
      config: { ...this.currentConfig },
      loadedPanels: Array.from(this.loadedPanels.keys())
    };
  }

  getAllMeshNames() {
    const names = [];
    this.loadedPanels.forEach(({ model }) => {
      model.traverse(child => {
        if (child.isMesh && child.name) {
          names.push(child.name);
        }
      });
    });
    return names;
  }

  isReady() {
    return this.isInitialized && !this.isLoading;
  }

  dispose() {
    try {
      // Clear panels
      this.clearPanels();
      
      // Clear caches
      this.materialManager?.clearCache?.();
      
      // Dispose managers
      this.cameraControls?.dispose?.();
      this.modelLoader?.dispose?.();
      this.sceneManager?.dispose?.();
      
      // Clear references
      this.sceneManager = null;
      this.modelLoader = null;
      this.materialManager = null;
      this.cameraControls = null;
      this.configData = null;
      this.loadingSequence = [];
      this.materialGroups = {};
      this.currentConfig = {};
      
      this.isInitialized = false;
      this.isLoading = false;
      
      log('SignatureConfigurator3D disposed');
    } catch (error) {
      console.error('Error disposing SignatureConfigurator3D:', error);
    }
  }
}

// Export for both module and non-module usage
window.SignatureConfigurator3D = SignatureConfigurator3D;

export { SignatureConfigurator3D };

